// File: `typescript/apps/front-end/src/main/react/user/useUserSafetyActions.tsx`
import React from "react";
import { UserMinus, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import logger from "@/lib/logger";
import { ClientFactory } from "@/main/common/ClientFactory";
import { MetaHandler } from "@/main/common/MetaHandler";
import { UserSafetyApi } from "@/main/common/UserSafetyApi";
import { useSupportUsername } from "@/main/navigation/useSupportUsername";
import { isSupportParticipant } from "@/main/react/header/sidebar/chat/chatShared";
import { showYesNoDialog } from "@/main/react/Dialog/YesNoDialog";
import type { ContextualAccountAccessDialogContext } from "@/main/react/account_access/AccountAccessDialogContext";
import type { DropdownItem } from "@/main/react/buttons/CtaButton";
import { Localizer } from "@/main/react/Types";

/**
 * Block / mute for ONE other account: the state, the confirm dialogs, the optimistic rollback and the
 * coalescer invalidation, in one place.
 *
 * <p>Extracted from `UserButtons` on 2026-08-10 so the comment and discussion overflow menus can offer the
 * same two actions without a second copy of the sequence. Two copies of a confirm + optimistic write +
 * `UserSafetyApi.invalidate` chain drift silently, and the half that drifts is the one nobody opens on a
 * profile page. Design:
 * `designs/01-now/2026-08-10-member-safety-tools-and-report-visibility-design.md` slice A.
 *
 * <p><b>The fetch is opt-in through `enabled`, and that is load-bearing on a content surface.</b>
 * `UserSafetyApi.getSafetyState` is coalesced per username, but a discussion with 40 comments across 20
 * authors still issues 20 requests if every card asks on mount. Content menus pass `enabled: menuOpen`;
 * `UserButtons` leaves the default `true` because the profile header renders the follow and message
 * controls from `blockedByViewer` / `viewerBlockedByUser` before any menu is opened.
 *
 * <p>No new localization keys: every string here is one the profile menu already ships in all 24 locales.
 */
export type UseUserSafetyActionsOptions = {
    /** The account these controls act on. Absent/blank disables the whole hook. */
    username?: string | null;
    localizer: Localizer;
    /** Fetch the safety state. Default `true`; pass the menu's open state on a per-item content menu. */
    enabled?: boolean;
    /**
     * Another action on the SAME surface is in flight. `UserButtons` shares one `busy` flag across follow,
     * ban and the join-request verbs, and block/mute have always participated in it — so the flag is passed
     * in rather than duplicated, and the caller ORs it with the `busy` returned below.
     */
    externallyBusy?: boolean;
    /**
     * Fired once, after a block has been written and confirmed. Never on unblock, never on mute, and
     * never on a failure that rolled back.
     *
     * <p><b>Deliberately content-free, and that is the whole design of it (D1, 2026-08-14).</b> The
     * caller that has a group and knows whether the viewer leads it is `UserButtons`; this hook does
     * not, and must not learn. It now runs inside every comment's action row, where there is no group
     * and no leadership, so a group-aware branch here would make the entire thought and discussion
     * button surface carry a concept it has no use for. Content menus simply pass nothing.
     */
    onBlocked?: (username: string) => void;
};

export type UserSafetyActions = {
    blockedByViewer: boolean;
    viewerBlockedByUser: boolean;
    mutedByViewer: boolean;
    /** The viewer is the account these controls would act on. */
    isSelf: boolean;
    /** The target is the configured support account (D29). */
    isSupportAccount: boolean;
    /**
     * Whether the block / mute pair may be rendered at all.
     *
     * <p>D29: on your own content and on the support account they are HIDDEN, not left inert. Blocking
     * support does nothing — `ChatService`'s D10 carve-out skips `isEitherDirectionBlocked` for a support
     * pair — and a control that looks like it acted and silently did not is worse than no control, because
     * the member believes they are unreachable until the next message arrives. Reporting stays offered on
     * both, because reporting does exactly what it says.
     */
    canOfferSafetyControls: boolean;
    /** A block/mute write is in flight. */
    busy: boolean;
    toggleBlock: () => Promise<void>;
    toggleMute: () => Promise<void>;
};

export function useUserSafetyActions(options: UseUserSafetyActionsOptions): UserSafetyActions {
    const { localizer, enabled = true, externallyBusy = false, onBlocked } = options;

    const [blockedByViewer, setBlockedByViewer] = React.useState(false);
    const [viewerBlockedByUser, setViewerBlockedByUser] = React.useState(false);
    const [mutedByViewer, setMutedByViewer] = React.useState(false);
    const [busy, setBusy] = React.useState(false);

    const username = typeof options.username === "string" ? options.username.trim() : "";
    const loggedInUsername = MetaHandler.getLoggedInUsername();
    const isSelf = Boolean(loggedInUsername && username && loggedInUsername === username);
    // D29. Resolved from the server-seeded shell state, never from a hardcoded username — the same single
    // source the chat alias reads, so the two can never disagree about which account this is.
    const isSupportAccount = isSupportParticipant(username, useSupportUsername());
    const isBusy = busy || externallyBusy;

    React.useEffect(() => {
        if (!username || isSelf) {
            setBlockedByViewer(false);
            setViewerBlockedByUser(false);
            setMutedByViewer(false);
            return;
        }

        // Nothing to read for a logged-out visitor: all three flags are relationships the VIEWER is one
        // half of, so with no viewer the answer is definitionally "no relationship". The menu items still
        // render for them and route through the account-access dialog on click, exactly like Report.
        if (!enabled || !loggedInUsername) {
            return;
        }

        let cancelled = false;
        // Goes through UserSafetyApi, not the generated client, so the several cards that can show the
        // same person on one page share a single request (see the duplicate-mount design). That coalescing
        // is what makes a per-comment menu affordable at all.
        void UserSafetyApi.getSafetyState(username)
            .then((state) => {
                if (cancelled || !state) {
                    return;
                }
                setBlockedByViewer(state.blockedByViewer);
                setViewerBlockedByUser(state.viewerBlockedByUser);
                setMutedByViewer(state.mutedByViewer);
            })
            .catch((err) => {
                // A 4xx means this username does not resolve for the viewer — an expected protocol
                // outcome (the safety flags simply stay false), not something anyone can act on.
                // Reserve `error` for 5xx/network failures so the production FRONTEND error log and
                // the Playwright client-error oracle keep meaning "something is actually broken".
                const status = (err as { response?: { status?: number } })?.response?.status;
                const expected = typeof status === "number" && status >= 400 && status < 500;
                const log = expected ? logger.warn : logger.error;
                log("Failed to load user safety state", { err, username });
            });

        return () => {
            cancelled = true;
        };
    }, [enabled, isSelf, loggedInUsername, username]);

    // Resolved at CALL time rather than in a `useMemo` at mount: this hook now runs inside every comment's
    // action row, and a mount-time `ClientFactory.getAjaxUserRestControllerApi()` would make the whole
    // thought/discussion button surface depend on that client existing even on the paths that never touch
    // safety at all.
    const userApi = () => ClientFactory.getAjaxUserRestControllerApi();

    /**
     * QD-15: Undo on the unblock/unmute toast puts back the block or mute the member had a moment ago.
     * No confirm dialog: the member confirmed this relationship when they first set it, and the toast is
     * the one-tap way back from a mis-tap. It does NOT fire `onBlocked` (the group-ban suggestion is for a
     * NEW block, not a restored one).
     */
    const undoRelease = async (kind: "block" | "mute") => {
        if (!username) return;
        const setFlag = kind === "block" ? setBlockedByViewer : setMutedByViewer;
        setBusy(true);
        setFlag(true);
        try {
            if (kind === "block") {
                await userApi().blockUser({ targetUsername: String(username) });
            } else {
                await userApi().muteUser({ targetUsername: String(username) });
            }
            UserSafetyApi.invalidate(String(username));
            toast.success(localizer(kind === "block" ? "user.safety.block.block-success" : "user.safety.mute.mute-success"));
        } catch (err) {
            setFlag(false);
            logger.error("Undo unblock/unmute failed", { err, username });
            toast.error(localizer("error.unexpected"));
        } finally {
            setBusy(false);
        }
    };

    const toggleBlock = async () => {
        if (isBusy || isSelf || !username) return;

        if (!blockedByViewer) {
            showYesNoDialog({
                localizer,
                title: localizer("user.safety.block.block"),
                message: <div>{localizer("user.safety.block.confirm")}</div>,
                yesLabel: localizer("user.safety.block.block"),
                yesIcon: UserMinus,
                noLabel: localizer("literal.cancel"),
                noIcon: X,
                destructive: true,
                // Both halves are named so a browser spec can drive the confirm through a stable hook
                // rather than through localized copy, which a rename would silently break.
                yesPlaywrightId: "user-safety-block-confirm",
                noPlaywrightId: "user-safety-block-cancel",
                onYes: async () => {
                    setBusy(true);
                    const previousBlockedByViewer = blockedByViewer;
                    setBlockedByViewer(true);

                    try {
                        await userApi().blockUser({ targetUsername: String(username) });
                        // The coalescer holds a resolved safety state for a couple of seconds; drop it
                        // so a card mounting right after this action does not read the pre-block value.
                        UserSafetyApi.invalidate(String(username));
                        toast.success(localizer("user.safety.block.block-success"));
                        // After the success toast, so a caller's follow-up (D1's group-ban suggestion)
                        // reads as the next thing that happened rather than pre-empting the confirmation.
                        onBlocked?.(String(username));
                    } catch (err) {
                        setBlockedByViewer(previousBlockedByViewer);
                        logger.error("Block/unblock failed", { err, username });
                        // YesNoDialog surfaces the failure banner on re-throw; don't also toast.
                        throw err;
                    } finally {
                        setBusy(false);
                    }
                },
            });
            return;
        }

        // Reaching here means the user is currently blocked (the not-blocked case is
        // fully handled by the confirmation dialog above, which returns), so this path
        // only ever unblocks.
        setBusy(true);
        setBlockedByViewer(false);

        try {
            await userApi().unblockUser(String(username));
            UserSafetyApi.invalidate(String(username));
            toast.success(localizer("user.safety.block.unblock-success"), {
                action: {
                    // QD-15. The stable hook rides inside the label (the unsave-undo precedent).
                    label: <span data-playwright-id="user-safety-unblock-undo-action">{localizer("literal.undo")}</span>,
                    onClick: () => { void undoRelease("block"); },
                },
            });
        } catch (err) {
            setBlockedByViewer(true);
            logger.error("Block/unblock failed", { err, username });
            toast.error(localizer("user.safety.block.unblock-failed"));
        } finally {
            setBusy(false);
        }
    };

    const toggleMute = async () => {
        if (isBusy || isSelf || !username) return;

        if (!mutedByViewer) {
            showYesNoDialog({
                localizer,
                title: localizer("user.safety.mute.mute"),
                message: <div>{localizer("user.safety.mute.confirm")}</div>,
                yesLabel: localizer("user.safety.mute.mute"),
                yesIcon: UserMinus,
                noLabel: localizer("literal.cancel"),
                noIcon: X,
                yesPlaywrightId: "user-safety-mute-confirm",
                noPlaywrightId: "user-safety-mute-cancel",
                onYes: async () => {
                    setBusy(true);
                    const previousMutedByViewer = mutedByViewer;
                    setMutedByViewer(true);

                    try {
                        await userApi().muteUser({ targetUsername: String(username) });
                        UserSafetyApi.invalidate(String(username));
                        toast.success(localizer("user.safety.mute.mute-success"));
                    } catch (err) {
                        setMutedByViewer(previousMutedByViewer);
                        logger.error("Mute/unmute failed", { err, username });
                        // YesNoDialog surfaces the failure banner on re-throw; don't also toast.
                        throw err;
                    } finally {
                        setBusy(false);
                    }
                },
            });
            return;
        }

        // Reaching here means the user is currently muted (the not-muted case is fully
        // handled by the confirmation dialog above, which returns), so this path only
        // ever unmutes.
        setBusy(true);
        setMutedByViewer(false);

        try {
            await userApi().unmuteUser(String(username));
            UserSafetyApi.invalidate(String(username));
            toast.success(localizer("user.safety.mute.unmute-success"), {
                action: {
                    label: <span data-playwright-id="user-safety-unmute-undo-action">{localizer("literal.undo")}</span>,
                    onClick: () => { void undoRelease("mute"); },
                },
            });
        } catch (err) {
            setMutedByViewer(true);
            logger.error("Mute/unmute failed", { err, username });
            toast.error(localizer("user.safety.mute.unmute-failed"));
        } finally {
            setBusy(false);
        }
    };

    return {
        blockedByViewer,
        viewerBlockedByUser,
        mutedByViewer,
        isSelf,
        isSupportAccount,
        canOfferSafetyControls: Boolean(username) && !isSelf && !isSupportAccount,
        busy,
        toggleBlock,
        toggleMute,
    };
}

/**
 * The two overflow-menu entries, built once so the profile menu and the content menus cannot drift in
 * label, icon, destructiveness, item id or ordering. Mute first, then block — least to most severe, which
 * is the order `UserButtons` has always used.
 *
 * <p>Returns an empty array when the pair must not be offered, so a caller can spread it unconditionally.
 */
export function buildUserSafetyDropdownItems(
    safety: UserSafetyActions,
    localizer: Localizer,
    accountAccessDialog: ContextualAccountAccessDialogContext,
): DropdownItem[] {
    if (!safety.canOfferSafetyControls) {
        return [];
    }

    const stopEvent = (ev?: Event) => {
        try {
            ev?.preventDefault();
            ev?.stopPropagation();
        } catch {
        }
    };

    return [
        {
            id: "mute-toggle",
            icon: safety.mutedByViewer ? UserPlus : UserMinus,
            text: safety.mutedByViewer ? localizer("user.safety.mute.unmute") : localizer("user.safety.mute.mute"),
            onClick: (ev?: Event) => {
                stopEvent(ev);
                void safety.toggleMute();
            },
            requiresLogin: true,
            accountAccessDialog,
        },
        {
            id: "block-toggle",
            icon: safety.blockedByViewer ? UserPlus : UserMinus,
            text: safety.blockedByViewer ? localizer("user.safety.block.unblock") : localizer("user.safety.block.block"),
            destructive: !safety.blockedByViewer,
            onClick: (ev?: Event) => {
                stopEvent(ev);
                void safety.toggleBlock();
            },
            requiresLogin: true,
            accountAccessDialog,
        },
    ];
}
